// Country-pack governance (read-only). The statutory packs (PAYE/NSSF/SDL/…) that
// drive payroll per jurisdiction. payroll-service platform endpoint via the gateway.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/payroll/platform/country-packs`;

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface IdentifierField {
  label: string;
  regex?: string;
  required?: boolean;
}
export interface IdentifierSchema {
  employee?: Record<string, IdentifierField>;
  employer?: Record<string, IdentifierField>;
}

export type PackStatus = 'draft' | 'in_review' | 'published' | 'superseded';

export interface CountryPack {
  id: string;
  country: string;
  region?: string | null;
  name: string;
  version: number;
  identifier_schema: IdentifierSchema;
  effective_from: string;
  effective_to?: string | null;
  active: boolean;
  status?: PackStatus;
  rule_count: number;
  updated_at: string;
}

export interface PackBand {
  from: string;
  to?: string | null;
  rate: string;
  label?: string;
}
export interface PackRuleConfig {
  name?: string;
  component_type?: string;
  basis?: string;
  statutory?: boolean;
  rate?: string;
  amount?: string;
  cap?: string | null;
  bands?: PackBand[];
  when?: string;
}
export interface PackRule {
  code: string;
  kind: string; // bracket | percent | fixed | formula
  order_index: number;
  payer: string; // employee | employer
  pre_tax: boolean;
  config: PackRuleConfig;
}
export interface CountryPackDetail extends CountryPack {
  rules: PackRule[];
}

export async function listCountryPacks(): Promise<CountryPack[]> {
  const { data } = await axios.get(BASE, { headers: { ...authHeader() } });
  return data.packs ?? [];
}

export async function getCountryPack(id: string): Promise<CountryPackDetail> {
  const { data } = await axios.get(`${BASE}/${id}`, { headers: { ...authHeader() } });
  return data;
}

// --- Authoring (Phase 3 frontend) --------------------------------------------

// A rule as authored (what the draft editor sends back).
export interface DraftRule {
  code: string;
  kind: 'bracket' | 'percent' | 'fixed' | 'formula';
  order_index: number;
  payer: 'employee' | 'employer';
  pre_tax: boolean;
  config: PackRuleConfig;
}

export interface CreateDraftBody {
  country: string;
  region?: string | null;
  name: string;
  clone_from_id?: string;
  rules?: DraftRule[];
  identifier_schema?: IdentifierSchema;
  reason?: string;
}

export interface DraftContentBody {
  name: string;
  region?: string | null;
  identifier_schema?: IdentifierSchema;
  rules: DraftRule[];
  reason?: string;
}

export interface PackValidation {
  errors: string[];
  warnings: string[];
}

export interface PreviewCalc {
  net: string;
  gross: string;
  employer_cost: string;
  taxable_income: string;
  lines: { code: string; payer: string; amount: string }[];
}
export interface PreviewSample {
  gross: string;
  draft: PreviewCalc | null;
  current: PreviewCalc | null;
}
export interface PackPreview {
  country: string;
  currency: string;
  has_current: boolean;
  samples: PreviewSample[];
}

const H = () => ({ headers: { ...authHeader() } });

export async function createCountryPackDraft(body: CreateDraftBody): Promise<{ id: string }> {
  const { data } = await axios.post(BASE, body, H());
  return data;
}
export async function updateCountryPackDraft(id: string, body: DraftContentBody): Promise<void> {
  await axios.put(`${BASE}/${id}`, body, H());
}
export async function submitCountryPack(id: string): Promise<void> {
  await axios.post(`${BASE}/${id}/submit`, {}, H());
}
export async function publishCountryPack(id: string, effective_from: string, reason?: string): Promise<void> {
  await axios.post(`${BASE}/${id}/publish`, { effective_from, reason }, H());
}
export async function rejectCountryPack(id: string, reason?: string): Promise<void> {
  await axios.post(`${BASE}/${id}/reject`, { reason }, H());
}
export async function discardCountryPack(id: string): Promise<void> {
  await axios.delete(`${BASE}/${id}`, H());
}
export async function validateCountryPack(id: string): Promise<PackValidation> {
  const { data } = await axios.post(`${BASE}/${id}/validate`, {}, H());
  return data;
}
export async function previewCountryPack(id: string, sample_gross?: string[]): Promise<PackPreview> {
  const { data } = await axios.post(`${BASE}/${id}/preview`, { sample_gross }, H());
  return data;
}
// Bulk import: create a draft from an uploaded YAML/JSON pack document.
export async function importCountryPack(file: File): Promise<{ id: string }> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${BASE}/import`, form, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadCountryPackSignoff(
  id: string,
  file: File,
  meta: { authority?: string; signed_on?: string; notes?: string },
): Promise<{ id: string }> {
  const form = new FormData();
  form.append('file', file);
  if (meta.authority) form.append('authority', meta.authority);
  if (meta.signed_on) form.append('signed_on', meta.signed_on);
  if (meta.notes) form.append('notes', meta.notes);
  const { data } = await axios.post(`${BASE}/${id}/signoff`, form, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
